// Telegram WebApp SDK wrapper.
// Exposes a typed handle to window.Telegram.WebApp and writes theme params as CSS variables.

export interface TgThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  destructive_text_color?: string;
}

export interface TgWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
    };
  };
  colorScheme: 'light' | 'dark';
  themeParams: TgThemeParams;
  ready: () => void;
  expand: () => void;
  close: () => void;
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  };
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    setText: (s: string) => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  onEvent: (event: string, cb: () => void) => void;
  /** Bot API 7.7+: turns off the swipe down that closes the app. Absent on older clients. */
  disableVerticalSwipes?: () => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export function tg(): TgWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function applyTelegramTheme(): void {
  const t = tg();
  if (!t) return;
  const root = document.documentElement;
  const set = (k: keyof TgThemeParams, cssVar: string) => {
    const v = t.themeParams[k];
    if (v) root.style.setProperty(cssVar, v);
  };
  set('bg_color',              '--tg-theme-bg-color');
  set('text_color',            '--tg-theme-text-color');
  set('hint_color',            '--tg-theme-hint-color');
  set('link_color',            '--tg-theme-link-color');
  set('button_color',          '--tg-theme-button-color');
  set('button_text_color',     '--tg-theme-button-text-color');
  set('secondary_bg_color',    '--tg-theme-secondary-bg-color');
  set('header_bg_color',       '--tg-theme-header-bg-color');
  set('accent_text_color',     '--tg-theme-accent-text-color');
  set('section_bg_color',      '--tg-theme-section-bg-color');
  set('destructive_text_color','--tg-theme-destructive-text-color');
  root.classList.toggle('dark', t.colorScheme === 'dark');
}

export function bootTelegram(): void {
  const t = tg();
  if (!t) return;
  t.ready();
  t.expand();
  // The app scrolls inside its own box (lib/viewport.ts), so the document
  // is always at the top — where Telegram reads a downward swipe as "close
  // the app". Off, so a scroll down a long statement stays a scroll.
  t.disableVerticalSwipes?.();
  applyTelegramTheme();
  t.onEvent('themeChanged', applyTelegramTheme);
}
